import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Lang, t } from "../i18n";
import { cookiePath } from "../legal";

type CookieConsentValue = "accepted" | "declined";
type ConsentState = CookieConsentValue | null | "loading";

declare global {
  interface Window {
    __claweaseCookieConsentKey?: string;
    __claweaseGetCookieConsent?: () => CookieConsentValue | string | null;
    __claweaseSetCookieConsent?: (value: CookieConsentValue) => void;
  }
}

const fallbackCookieConsentKey = "clawease_cookie_consent";

function readStoredConsent(): CookieConsentValue | null {
  if (typeof window === "undefined") return null;

  const runtimeValue = window.__claweaseGetCookieConsent?.();
  if (runtimeValue === "accepted" || runtimeValue === "declined") return runtimeValue;

  try {
    const value = window.localStorage.getItem(window.__claweaseCookieConsentKey || fallbackCookieConsentKey);
    return value === "accepted" || value === "declined" ? value : null;
  } catch {
    return null;
  }
}

function resolveLang(pathname: string): Lang {
  if (pathname.startsWith("/hk/")) return "zh";
  if (pathname.startsWith("/cn/ai-receptionist") || pathname.startsWith("/cn/") || pathname.startsWith("/clawease/cn")) return "zh";
  return "en";
}

export function CookieConsentBanner() {
  const { pathname } = useLocation();
  const lang = useMemo(() => resolveLang(pathname), [pathname]);
  const hkCopy: Record<string, string> = {
    title: "Cookie 使用選擇",
    desc: "我們使用可選 Cookie 及類似技術了解網站瀏覽情況、衡量廣告成效並改善你的體驗。你可以接受或拒絕這些可選 Cookie。",
    policy: "查看 Cookie 政策", reject: "拒絕", accept: "允許",
  };
  const consentText = (key: string) => pathname.startsWith("/hk/") ? hkCopy[key] : t(lang, `cookieConsent.${key}`);
  const [consent, setConsent] = useState<ConsentState>("loading");

  useEffect(() => {
    const syncConsent = () => setConsent(readStoredConsent());

    syncConsent();
    window.addEventListener("storage", syncConsent);
    window.addEventListener("clawease-cookie-consent-change", syncConsent);

    return () => {
      window.removeEventListener("storage", syncConsent);
      window.removeEventListener("clawease-cookie-consent-change", syncConsent);
    };
  }, []);

  function chooseConsent(value: CookieConsentValue) {
    if (typeof window.__claweaseSetCookieConsent === "function") {
      window.__claweaseSetCookieConsent(value);
    } else {
      try {
        window.localStorage.setItem(window.__claweaseCookieConsentKey || fallbackCookieConsentKey, value);
      } catch {
        // If storage is blocked, still hide the banner for this page view.
      }
    }

    setConsent(value);
  }

  if (consent === "loading" || consent) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[90] w-[min(420px,calc(100vw-32px))] rounded-[22px] border border-[#eaddea] bg-white/95 p-4 text-[#100f12] shadow-[0_22px_70px_rgba(46,22,152,.18)] backdrop-blur-xl sm:p-5">
      <div>
        <p className="text-[15px] font-extrabold leading-6">{consentText("title")}</p>
        <p className="mt-1 text-[13px] font-semibold leading-6 text-[#6b5f70]">{consentText("desc")}</p>
        <Link to={cookiePath(lang)} className="mt-2 inline-flex text-[13px] font-extrabold text-[#5a26d6] hover:text-[#7b2fd0]">
          {consentText("policy")}
        </Link>
      </div>

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={() => chooseConsent("declined")}
          className="min-h-[42px] rounded-full border border-[#e5d8e8] bg-white px-5 text-[13px] font-extrabold text-[#4f4554] transition hover:border-[#c8b6d3] hover:bg-[#fbf8fd]"
        >
          {consentText("reject")}
        </button>
        <button
          type="button"
          onClick={() => chooseConsent("accepted")}
          className="min-h-[42px] rounded-full bg-gradient-to-r from-[#5a26d6] to-[#d728c9] px-5 text-[13px] font-extrabold text-white shadow-[0_12px_30px_rgba(90,38,214,.24)] transition hover:translate-y-[-1px]"
        >
          {consentText("accept")}
        </button>
      </div>
    </div>
  );
}
